import React, { useState, useEffect } from 'react';
import { Plus, UtensilsCrossed, Trash2, Edit, Filter, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { menuService } from '@/services/menu.service';
import { eventService } from '@/services/event.service';
import { vendorService } from '@/services/vendor.service';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';
import { canAddResource, isTrialExpired } from '@/utils/trialUtils';
import { BlockedActionDialog } from '@/components/BlockedActionDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function MenuModule({ weddingId, wedding }) {
  const { activeOrg, userRole } = useAuth();
  const canManage = permissions.canManageMenu(userRole);
  const isReadOnly = permissions.isReadOnly(userRole);
  const [menuItems, setMenuItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [eventVendors, setEventVendors] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isEventLocked, setIsEventLocked] = useState(false); // true only when opened from per-event "Add Item"
  const [formData, setFormData] = useState({
    eventName: '',
    course: 'Starter',
    itemName: '',
    description: '',
    dietaryInfo: 'Vegetarian'
  });
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);

  // Check trial limits
  const menuItemLimitCheck = React.useMemo(() => {
    if (!wedding) return { canAdd: true, message: '', limit: null };
    return canAddResource(wedding, 'menu_items', menuItems.length);
  }, [wedding, menuItems.length]);
  
  const isBlocked = React.useMemo(() => {
    if (!wedding) return false;
    return !menuItemLimitCheck.canAdd || isTrialExpired(wedding);
  }, [wedding, menuItemLimitCheck]);

  // Load events, vendors and menu items
  useEffect(() => {
    loadData();
  }, [weddingId, activeOrg]);

  const loadData = async () => {
    if (!weddingId || !activeOrg?.id) return;
    
    try {
      setLoading(true);
      const [eventsData, itemsData, vendorsData, eventVendorsData] = await Promise.all([
        eventService.getEvents(weddingId, activeOrg.id),
        menuService.getMenuItems(weddingId, activeOrg.id),
        vendorService.getVendors(weddingId, activeOrg.id),
        menuService.getEventVendors(weddingId, activeOrg.id)
      ]);
      
      setEvents(eventsData);
      setMenuItems(itemsData);
      
      // Filter catering vendors
      const cateringVendors = vendorsData.filter(v => v.category === 'Catering');
      setVendors(cateringVendors);
      
      // Build event-vendor map
      const evMap = {};
      eventVendorsData.forEach(ev => {
        evMap[ev.event_name] = ev.vendor_id;
      });
      setEventVendors(evMap);
    } catch (error) {
      console.error('Failed to load menu data:', error);
      toast.error('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = selectedEvent === 'all' 
    ? menuItems 
    : menuItems.filter(item => item.event_name === selectedEvent);

  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.event_name]) {
      acc[item.event_name] = {};
    }
    if (!acc[item.event_name][item.course]) {
      acc[item.event_name][item.course] = [];
    }
    acc[item.event_name][item.course].push(item);
    return acc;
  }, {});

  const handleAddItem = async () => {
    if (!formData.eventName || !formData.itemName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Transform camelCase to snake_case for backend
      const menuData = {
        event_name: formData.eventName,
        course: formData.course,
        item_name: formData.itemName,
        description: formData.description,
        dietary_info: formData.dietaryInfo
      };
      
      await menuService.createMenuItem(weddingId, activeOrg.id, menuData);
      await loadData();
      toast.success('Menu item added successfully');
      setIsAddDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add menu item:', error);
      toast.error('Failed to add menu item');
    }
  };

  const handleUpdateItem = async () => {
    if (!formData.itemName) {
      toast.error('Item name is required');
      return;
    }

    try {
      // Transform camelCase to snake_case for backend
      const menuData = {
        event_name: formData.eventName,
        course: formData.course,
        item_name: formData.itemName,
        description: formData.description,
        dietary_info: formData.dietaryInfo
      };
      
      await menuService.updateMenuItem(editingItem.id, menuData);
      await loadData();
      toast.success('Menu item updated successfully');
      setIsEditDialogOpen(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update menu item:', error);
      toast.error('Failed to update menu item');
    }
  };

  const handleVendorSelect = async (eventName, vendorId) => {
    try {
      await menuService.setEventVendor(weddingId, eventName, vendorId);
      setEventVendors(prev => ({
        ...prev,
        [eventName]: vendorId
      }));
      const vendor = vendors.find(v => v.id === vendorId);
      if (vendor) {
        toast.success(`${vendor.name} selected for ${eventName}`);
      }
    } catch (error) {
      console.error('Failed to set vendor:', error);
      toast.error('Failed to set vendor');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await menuService.deleteMenuItem(itemId);
      await loadData();
      toast.success('Menu item deleted successfully');
    } catch (error) {
      console.error('Failed to delete menu item:', error);
      toast.error('Failed to delete menu item');
    }
  };

  const resetForm = () => {
    setFormData({
      eventName: '',
      course: 'Starter',
      itemName: '',
      description: '',
      dietaryInfo: 'Vegetarian'
    });
    setIsEventLocked(false);
  };

  const handleAddItemForEvent = (eventName) => {
    if (isBlocked) {
      setBlockedDialogOpen(true);
      return;
    }
    setIsEventLocked(true);
    setFormData({
      eventName: eventName,
      course: 'Starter',
      itemName: '',
      description: '',
      dietaryInfo: 'Vegetarian'
    });
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      eventName: item.event_name,
      course: item.course,
      itemName: item.item_name,
      description: item.description,
      dietaryInfo: item.dietary_info
    });
    setIsEditDialogOpen(true);
  };

  const getDietaryBadgeColor = (dietary) => {
    switch (dietary) {
      case 'Vegetarian': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Non-Vegetarian': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Vegan': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'Jain': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold gradient-text mb-1">Menu Planning</h2>
          <p className="text-sm text-muted-foreground">
            Plan menu items for each event
          </p>
        </div>
        {canManage && (
          <Button onClick={() => {
            resetForm();
            setIsEventLocked(false);
            setIsAddDialogOpen(true);
          }} className="gradient-primary w-full sm:w-auto" disabled={isReadOnly}>
            <Plus className="h-4 w-4" />
            Add Menu Item
          </Button>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map(event => (
              <SelectItem key={event.id} value={event.name}>
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Menu Items */}
      {Object.keys(groupedItems).length === 0 ? (
        <Card className="p-12 text-center">
          <UtensilsCrossed className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No menu items yet</h3>
          <p className="text-muted-foreground mb-4">
            Start planning your wedding menu by adding dishes for each event
          </p>
          {canManage && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={() => {
                        resetForm();
                        setIsAddDialogOpen(true);
                      }} 
                      className="gradient-primary" 
                      disabled={isReadOnly || isBlocked}
                    >
                      <Plus className="h-4 w-4" />
                      Add First Item
                    </Button>
                  </span>
                </TooltipTrigger>
                {isBlocked && (
                  <TooltipContent>
                    <p>🔒 Trial limit reached<br />Upgrade for more menu items</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedItems).map(([eventName, courses]) => {
            const selectedVendor = eventVendors[eventName] 
              ? vendors.find(v => v.id === eventVendors[eventName])
              : null;
            
            return (
              <Card key={eventName} className="p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 pb-4 border-b">
                  <div className="flex items-center gap-4">
                    <h3 className="text-xl font-bold gradient-text">{eventName}</h3>
                    {canManage && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button 
                                size="sm" 
                                onClick={() => handleAddItemForEvent(eventName)}
                                className="gradient-primary"
                                disabled={isReadOnly || isBlocked}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Item
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {isBlocked && (
                            <TooltipContent>
                              <p>🔒 Trial limit reached<br />Upgrade for more menu items</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  
                  {/* Catering Vendor Selection */}
                  {vendors.length > 0 && (
                    <div className="flex items-center gap-2">
                      <ChefHat className="h-4 w-4 text-muted-foreground" />
                      <Select 
                        value={eventVendors[eventName] || ''} 
                        onValueChange={(value) => handleVendorSelect(eventName, value)}
                        disabled={!canManage || isReadOnly}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select caterer" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map(vendor => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {selectedVendor && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm">
                      <span className="font-medium">Caterer:</span> {selectedVendor.name}
                      <Badge variant="outline" className="ml-2">{selectedVendor.status}</Badge>
                    </p>
                  </div>
                )}
              
                {Object.entries(courses).map(([course, items]) => (
                <div key={course} className="mb-6 last:mb-0">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    {course}
                  </h4>
                  <div className="grid gap-3">
                    {items.map(item => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between p-4 rounded-lg border border-border hover:border-primary/50 transition-colors bg-card"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold">{item.item_name}</h5>
                            <Badge className={getDietaryBadgeColor(item.dietary_info)}>
                              {item.dietary_info}
                            </Badge>
                          </div>
                          {item.description && (
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          {canManage && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(item)}
                                disabled={isReadOnly}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.id)}
                                disabled={isReadOnly}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        setIsAddDialogOpen(open);
        setIsEditDialogOpen(open);
        if (!open) {
          resetForm();
          setEditingItem(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {isEditDialogOpen ? 'Edit Menu Item' : 'Add Menu Item'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Event</Label>
              <Select 
                value={formData.eventName} 
                onValueChange={(value) => setFormData({ ...formData, eventName: value })}
                disabled={isEventLocked && !isEditDialogOpen} // Disable only when opened from event card
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.name}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Course</Label>
              <Select value={formData.course} onValueChange={(value) => setFormData({ ...formData, course: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Starter">Starter</SelectItem>
                  <SelectItem value="Main Course">Main Course</SelectItem>
                  <SelectItem value="Dessert">Dessert</SelectItem>
                  <SelectItem value="Beverage">Beverage</SelectItem>
                  <SelectItem value="Snacks">Snacks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Item Name *</Label>
              <Input
                value={formData.itemName}
                onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                placeholder="e.g., Paneer Tikka"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the dish"
                rows={3}
              />
            </div>

            <div>
              <Label>Dietary Info</Label>
              <Select value={formData.dietaryInfo} onValueChange={(value) => setFormData({ ...formData, dietaryInfo: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                  <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                  <SelectItem value="Vegan">Vegan</SelectItem>
                  <SelectItem value="Jain">Jain</SelectItem>
                  <SelectItem value="Gluten-Free">Gluten-Free</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={isEditDialogOpen ? handleUpdateItem : handleAddItem}
              className="gradient-primary"
            >
              {isEditDialogOpen ? 'Update' : 'Add'} Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockedActionDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        resourceType="menu_items"
        limit={menuItemLimitCheck.limit}
        wedding={wedding}
        onUpgradeSuccess={loadData}
      />
    </div>
  );
}
